import type { SceneContent } from '../../presentation/types';

/**
 * Moderta AI — Research Space replaces the former Moderta Orchestrator slot (owner decision
 * 2026-10-10). Identity: Moderta-AI-Research-Space-Branding-and-UIUX.pdf. Facts: the owner's role
 * statement ("drafts proposed by tools, approved by people", docs/design-system/products/06) and
 * the claims already present in the original showcase content (docs/product-content/
 * MODERTA-ORCHESTRATE.md): focused AI roles, AI for judgment and automation for the repeatable,
 * people in the loop, approved work as the source of truth, suggestions that never overwrite.
 * The delivery-specific "design before generation" scene was retired: its code and test claims
 * describe Orchestrator, not Research Space. Further Research Space copy needs owner-supplied facts.
 */
export const researchSpace: SceneContent = {
  id: 'research-space',
  shortLabel: 'RESEARCH SPACE',
  title: 'Moderta AI — Research Space',
  seconds: 45,
  brand: 'research-space',
  bridgeIn: (context) => (context.isEnabled('mint')
    ? 'Beyond MINT, Moderta builds other major platforms. One starts with who decides what is true.'
    : 'Moderta builds major platforms of its own. One starts with who decides what is true.'),
  // The Research Space guideline rules out decoration: no particles on its canvas.
  atmosphere: () => ({ pattern: 'ambient', density: 0.04, opacity: 0, cameraZ: 14.4 }),
  script: {
    title: 'Moderta AI — Research Space',
    onScreen: ['Research · Requirements · Design audit · Test design · Engineering quality · Security review', 'Many focused tools. Many drafts.', 'Which version is true?', 'Moderta AI — Research Space', 'AI proposes — people approve.', 'AI · Reason · Suggest · Review · Explain', 'Automation · Template · Create · Connect · Validate · Move', 'The right tool for each step.'],
    visual: 'The stage turns to the Research Space canvas: flat #E0E0E0 with white cards and no shadows. Draft cards from six focused roles settle onto the canvas, each with a mono ID. The question appears over them. The cards clear and the rounded-square M monogram and Moderta AI lockup resolve, with the brand line in the title face. Two calm columns follow: AI for judgment, automation for the repeatable.',
    narration: [
      'Work now runs through many tools, and increasingly through AI. Not one generic chatbot, but focused roles: research, requirements, design audit, test design, engineering quality and security review.',
      'Every one of them can produce a draft. So which version becomes the truth?',
      'This is Moderta AI — Research Space.',
      'AI proposes. People approve.',
      'It uses AI where AI adds value: reasoning, suggesting, reviewing and explaining. And it uses plain automation where AI would only add cost and inconsistency: templates, structure, validation and moving work between tools. The objective is not maximum AI usage; it is maximum useful automation.',
    ],
    presenterNotes: [
      'Draft cards settle on the grey canvas. "Many focused tools. Many drafts."',
      'The question. Pause: "Which version is true?"',
      'Reveal the Research Space lockup. AI proposes, people approve.',
      'The two columns. "The right tool for each step." Do not call it a code generator.',
    ],
  },
};

export const researchApproval: SceneContent = {
  id: 'research-approval',
  shortLabel: 'APPROVAL',
  title: 'Research Space — Humans Approve the Truth',
  seconds: 50,
  brand: 'research-space',
  bridgeIn: 'Follow a single draft through it.',
  atmosphere: () => ({ pattern: 'ambient', density: 0.04, opacity: 0, cameraZ: 14.4 }),
  script: {
    title: 'Research Space — Humans Approve the Truth',
    onScreen: ['Proposed by a research tool', 'Needs review', 'Approve', 'Approved · Source of truth', 'Suggested edit — awaiting approval', 'Suggest, never overwrite.', 'Humans approve the truth.'],
    visual: 'One note card with a serif title and a mono ID enters from a research-tool chip with an amber "Needs review" dot. It travels to a review panel with a single Approve control; the control is pressed and the status turns to a green "Approved" dot with a lock and the label "Source of truth". A later suggestion card arrives and stops at the approved card\'s edge, held as "awaiting approval". The status colours appear only as dots and small badges, as the guideline requires. A caption marks the interface as a conceptual visualisation.',
    narration: [
      'Every draft starts as a proposal from a tool.',
      'People stay in the loop throughout. Nothing moves on until a person has reviewed it.',
      'When someone edits, approves or marks a draft final, that version becomes the source of truth.',
      'After that, AI can analyse it or suggest changes, but it never silently overwrites approved work.',
      'AI accelerates the work. Humans approve the truth.',
    ],
    presenterNotes: [
      'A draft arrives from a research tool. "Every draft starts as a proposal."',
      'It reaches review. "A person reviews it before anything moves on."',
      'Approved: the dot turns green and it locks. "That version becomes the source of truth."',
      'The suggestion stops short. "Suggest, never overwrite."',
      '"Humans approve the truth." Pause before moving on.',
    ],
  },
};
